import React, {
  useEffect,
  useRef,
  useState,
  ChangeEvent,
  useCallback,
} from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Outlet, useParams, useSearchParams } from "react-router-dom";
import { debounce, isEmpty } from "lodash";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getShareTemplatesWithMe } from "../utils/Services";
import SharedTemplate from "../components/SharedTemplate";
import EmptyNotes from "./EmptyNotes";
import { SUBSCRIPTION_STATUS, USER_ROLES } from "@/utils/constants";
import { useAppSelector } from "../hooks";
import { ISharedTemplate } from "../utils/Types";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FunnelX,
  Funnel,
  FileText,
  Stethoscope,
  Heartbeat,
  Thermometer,
  Brain,
  Users,
  Syringe,
  FirstAidKit,
  Pill,
  FunnelSimple,
} from "@phosphor-icons/react";
import { useAuthStore } from "@/utils/UseAuthStore";
import useIsMobile from "@/hooks/useIsMobile";

const Templates: React.FC = () => {
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(true);
  const [inputText, setInputText] = useState("");
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(20);
  const [templatesCount, setTemplatesCount] = useState(0);
  const user = useAppSelector((state) => state.user.user);
  const sortBy = useRef("created");
  const filterBy = useRef("");
  const [orderBy, setOrderBy] = useState("-");
  const [isFilter, setIsFilter] = useState(false);
  const [templates, setTemplates] = useState<ISharedTemplate[]>([]);
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn());
  const [refetchTemplates, setRefetchTemplates] = useState(false);

  const isMobile = useIsMobile();
  const { height, width } = useWindowDimensions();

  const { templateId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const side = searchParams.get("side");

  const categoryOptions = [
    {
      key: null,
      text: "Filter by category",
      icon: null,
    },
    {
      key: "history_present_illness",
      text: "History of Present Illness",
      icon: <FileText size={17} />,
    },
    {
      key: "review_of_systems",
      text: "Review of Systems",
      icon: <Stethoscope size={17} />,
    },
    {
      key: "physical_exam",
      text: "Physical Exam",
      icon: <Heartbeat size={17} />,
    },
    {
      key: "differential_diagnosis",
      text: "Differential Diagnosis",
      icon: <Thermometer size={17} />,
    },
    {
      key: "medical_decision_making",
      text: "Medical Decision Making",
      icon: <Brain size={17} />,
    },
    {
      key: "consults",
      text: "Consults",
      icon: <Users size={17} />,
    },
    {
      key: "procedures",
      text: "Procedures",
      icon: <Syringe size={17} />,
    },
    {
      key: "critical_care_note",
      text: "Critical Care Note",
      icon: <FirstAidKit size={17} />,
    },
    {
      key: "disposition",
      text: "Disposition",
      icon: <Pill size={17} />,
    },
  ];

  const options = {
    history_present_illness: {
      text: "History of Present Illness",
      icon: <FileText size={20} />,
    },
    review_of_systems: {
      text: "Review of Systems",
      icon: <Thermometer size={20} />,
    },
    physical_exam: {
      text: "Physical Exam",
      icon: <Stethoscope size={20} />,
    },
    differential_diagnosis: {
      text: "Differential Diagnosis",
      icon: <Heartbeat size={20} />,
    },
    medical_decision_making: {
      text: "Medical Decision Making",
      icon: <Brain size={20} />,
    },
    consults: {
      text: "Consults",
      icon: <Users size={20} />,
    },
    procedures: {
      text: "Procedures",
      icon: <Syringe size={20} />,
    },
    critical_care_note: {
      text: "Critical Care Note",
      icon: <FirstAidKit size={20} />,
    },
    disposition: {
      text: "Disposition",
      icon: <Pill size={20} />,
    },
  };

  const handleKeyDown = (event: KeyboardEvent): void => {
    const { ctrlKey, key, target } = event;

    if (
      target instanceof HTMLElement &&
      (target.tagName === "INPUT" || target.tagName === "TEXTAREA")
    ) {
      return;
    }

    if (!ctrlKey && key === "/") {
      const el = document.getElementById("search");

      if (el) {
        event.preventDefault();
        el.focus();
      }
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const fetchTemplates = async (
    search = "",
    replace: boolean = false,
    order = "-",
    sort = "created",
    off = 0,
    filter = ""
  ) => {
    if (replace) {
      setIsTemplatesLoading(true);
    }

    try {
      let response: {
        count: number;
        results: ISharedTemplate[];
      };

      response = await getShareTemplatesWithMe({
        ...(search && { search: search }),
        limit: limit,
        offset: off,
        ordering: order + sort,
        ...(filter && { category: filter }),
      });

      if (replace) {
        setTemplates(() => [...response?.results]);
      } else {
        setTemplates((prev) => [...prev, ...response?.results]);
      }
      setTemplatesCount(response?.count);
    } catch (error) {
    } finally {
      setIsTemplatesLoading(false);
    }
  };

  const fetchMoreNotes = () => {
    try {
      setOffset(offset + limit);
      fetchTemplates(
        inputText,
        false,
        orderBy,
        sortBy.current,
        offset + limit,
        filterBy.current
      );
    } catch (error) {}
  };

  const path = window.location.pathname;

  useEffect(() => {
    if (!templateId) {
      setIsTemplatesLoading(true);
      setOffset(0);
      if (!templates.length) {
        setRefetchTemplates(true);
      } else {
        setTemplates([]);
      }
    }
  }, [sortBy.current, path, orderBy, filterBy.current]);

  useEffect(() => {
    setRefetchTemplates(false);
    const res = setTimeout(async () => {
      if (
        !templates.length &&
        !isEmpty(user) &&
        (user?.is_staff ||
        user?.beta ||
        [
          USER_ROLES.STAFFADMIN,
          USER_ROLES.SUPERADMIN,
          USER_ROLES.ADMIN,
        ].includes(user.role!)
          ? true
          : ![SUBSCRIPTION_STATUS.IN_ACTIVE].includes(
              user?.subscription_status!
            ))
      ) {
        fetchTemplates(
          inputText,
          true,
          orderBy,
          sortBy.current,
          0,
          filterBy.current
        );
      }
    }, 500);

    return () => {
      clearTimeout(res);
    };
  }, [
    user,
    templates.length,
    filterBy.current,
    sortBy.current,
    refetchTemplates,
  ]);

  const debounceFn = useCallback(debounce(fetchTemplates, 500), []);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
    debounceFn(
      e.target.value,
      true,
      orderBy,
      sortBy.current,
      0,
      filterBy.current
    );
  };

  return (
    <div
      style={{
        height: `calc(100dvh - 65px)`,
      }}
      className="bg-gray-25/10 dark:bg-gray-900/10"
    >
      <TooltipProvider delayDuration={0}>
        <ResizablePanelGroup direction="horizontal">
          {(side || !templateId) && isLoggedIn && (
            <ResizablePanel
              defaultSize={20}
              collapsedSize={18}
              collapsible={true}
              minSize={18}
              maxSize={50}
              className={cn(
                isMobile || !templateId
                  ? "dark:bg-gray-950"
                  : "rounded-lg bg-white dark:bg-gray-950 my-4 ml-4 mr-2 border-[0.5px] border-gray-25 dark:border-gray-700 shadow-md",
                templateId ? "" : "flex flex-col items-center",
                width <= 1200 && templateId ? "hidden" : ""
              )}
            >
              <div
                className={
                  " " +
                  (!templateId && width <= 768
                    ? "w-full"
                    : templateId && width >= 768
                    ? ""
                    : "w-full md:max-w-[807px] ")
                }
              >
                {!templateId && width >= 768 && (
                  <div className="px-2 mt-5 flex items-center h-[32px]">
                    <span className="font-semibold text-xl">
                      Shared Templates
                    </span>
                  </div>
                )}
                {(templateId || width <= 768) && (
                  <div className="flex items-center px-4 border-b-[0.5px] h-[56px] bg-gray-25/20 dark:bg-gray-800/50">
                    <span className="font-semibold text-xl">
                      Shared Templates
                    </span>
                  </div>
                )}

                <div
                  className={
                    templateId || width <= 768
                      ? "w-full px-2"
                      : "flex items-center my-4 px-2"
                  }
                >
                  <div
                    className={
                      templateId || width <= 768
                        ? "mt-5 flex items-center justify-center"
                        : "min-w-[350px]"
                    }
                  >
                    <Input
                      id="search"
                      type="search"
                      placeholder="Type / for search."
                      onChange={handleInputChange}
                    />

                    {(!!templateId || width <= 768) && (
                      <Button
                        onClick={() => {
                          setIsFilter(!isFilter);
                        }}
                        variant={"ghost"}
                        className="ml-1 p-2"
                      >
                        {isFilter ? (
                          <FunnelX className="cursor-pointer" size={22} />
                        ) : (
                          <Funnel className="cursor-pointer" size={22} />
                        )}
                      </Button>
                    )}
                  </div>

                  <div
                    className={
                      "w-full flex items-center justify-center " +
                      (templateId || width <= 768
                        ? "flex-col my-2 "
                        : "mx-2 ") +
                      (templateId || width <= 768
                        ? isFilter
                          ? "note-filter-expand "
                          : "filter-contract contract"
                        : "")
                    }
                  >
                    <Select
                      onValueChange={(val) => {
                        setIsTemplatesLoading(true);
                        filterBy.current = val;
                        setOffset(0);
                        setTemplates([]);
                      }}
                    >
                      <SelectTrigger className="min-w-[180px]">
                        <SelectValue placeholder="Filter by category" />
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-gray-800">
                        <SelectGroup>
                          {categoryOptions.map((sort) => {
                            return (
                              <SelectItem
                                showIndicator={false}
                                key={sort.key}
                                className="px-2 hover:bg-gray-25 dark:hover:bg-gray-500"
                                value={sort.key!}
                              >
                                <div
                                  className={
                                    "flex items-center " +
                                    (sort.key === null
                                      ? "text-gray-500 dark:text-gray-50"
                                      : "")
                                  }
                                >
                                  <div className="mr-2">
                                    {sort.key === null ? (
                                      <FunnelSimple size={17} />
                                    ) : (
                                      sort.icon
                                    )}
                                  </div>
                                  {sort.text}
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div
                id="scrollableDiv"
                className="dark:text-white w-full"
                style={
                  templateId
                    ? {
                        overflow: "auto",
                        height: `calc(100dvh - ${isFilter ? 280 : 210}px)`,
                      }
                    : { overflow: "auto", height: "calc(100dvh - 130px)" }
                }
              >
                <div
                  className={
                    "mx-auto " +
                    (templateId ? "px-2 py-4" : "w-full md:max-w-[807px]")
                  }
                >
                  {isTemplatesLoading && (
                    <div className="px-2">
                      {[1, 2, 3].map((key) => (
                        <Skeleton
                          key={key}
                          className="bg-gray-25 dark:bg-gray-900 h-[80px] w-full rounded-xl my-2"
                        />
                      ))}
                    </div>
                  )}
                  {!isTemplatesLoading && templates.length ? (
                    <InfiniteScroll
                      dataLength={templates.length}
                      next={fetchMoreNotes}
                      hasMore={templatesCount > templates.length}
                      loader={
                        <div className="w-full text-center p-2">
                          <span className="loader w-5 h-5"></span>
                        </div>
                      }
                      scrollableTarget="scrollableDiv"
                    >
                      <ul role="list" className="space-y-3 mx-2">
                        {templates.map((template) => (
                          <SharedTemplate
                            key={template?.id}
                            template={template}
                            // @ts-ignore
                            icon={options[template?.template?.category!]?.icon}
                            category={
                              // @ts-ignore
                              options[template?.template?.category!]?.text
                            }
                          />
                        ))}
                      </ul>
                    </InfiniteScroll>
                  ) : (
                    <>
                      {!isTemplatesLoading && (
                        <div className="h-full flex items-center">
                          <EmptyNotes description="No Templates Found" />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </ResizablePanel>
          )}
          {templateId && (
            <>
              {width >= 768 && (side || !templateId) && isLoggedIn && (
                <div className="flex items-center">
                  <ResizableHandle withHandle />
                </div>
              )}
              <ResizablePanel defaultSize={60}>
                <Outlet />
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      </TooltipProvider>
    </div>
  );
};

export default Templates;
